import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SelectMe from 'react-select-me';

import { formatCurrency, formatValue } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import { useDvfYears } from '../../../hooks/useDvfYears';
import { setCompanyCommission } from '../../../store/AuthReducer';
import type { RootState } from '../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import Table from '../../common/Table';
import DismissibleModuleTutorialPopup from '../../common/Tutorials/DismissibleModuleTutorialPopup';
import ValuationModal from '../ValuationModal/ValuationModal';

type Filters = {
  ventesMin: number | null;
  ventesMax: number | null;
  caMin: number | null;
  caMax: number | null;
  commMin: number | null;
  commMax: number | null;
  avgSaleMin: number | null;
  scisMin: number | null;
  transactionnaire: string | null;
};

type CoproSalesRow = {
  id: number;
  immatriculation: string;
  name: string;
  address: string;
  cp: string;
  city: string;
  salesAmount: number;
  nbLots: number;
  nbSales: number;
  m2ForNbSales: number | null;
  salesAvgForM2: number | null;
};

const TRANSACTIONNAIRE_FONCTION_TYPE = 3; // see FonctionTypeUserEnum.TRANSACTIONNAIRE

function CoproDetailedList() {
  const [qfTooltip, setQfTooltip] = useState<{ visible: boolean; content: string; x: number; y: number }>({ visible: false, content: '', x: 0, y: 0 });
  const showQuickFilterTooltip = (event: React.MouseEvent, content: string) => {
    const clampedX = Math.max(8, Math.min(event.clientX, window.innerWidth - 8));
    const clampedY = Math.max(8, event.clientY - 8);
    setQfTooltip({ visible: true, content, x: clampedX, y: clampedY });
  };
  const hideQuickFilterTooltip = () => setQfTooltip((t) => ({ ...t, visible: false }));
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const { yearsOptions } = useDvfYears({ startYear: 2014 });
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const dispatch = useDispatch();
  const [commissionRate, setCommissionRate] = useState<number>(company?.commission ?? 5);
  const [yearlyExpanded, setYearlyExpanded] = useState<boolean>(false);
  const [lastDvfYear, setLastDvfYear] = useState<number | null>(null);
  const [yearlyStats, setYearlyStats] = useState<Record<number, { nbSales: number; nbLots: number; nbDpe: number; salesAmount: number; salesAveragePrice: number }>>({});
  const [yearlyStatus, setYearlyStatus] = useState<AsyncStatus>('IDLE');
  const [rows, setRows] = useState<CoproSalesRow[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sciCountByCopro, setSciCountByCopro] = useState<Record<number, number>>({});
  const [transactionnaireByCopro, setTransactionnaireByCopro] = useState<Record<number, string[]>>({});
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({ ventesMin: null, ventesMax: null, caMin: null, caMax: null, commMin: null, commMax: null, avgSaleMin: null, scisMin: null, transactionnaire: null });

  // Report modal
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportModalCopro, setReportModalCopro] = useState<{ id: number; name: string; immatriculation: string; address?: string; cp?: string; city?: string } | null>(null);
  const [selectedReport, setSelectedReport] = useState<'general' | 'sales' | 'dpe' | 'valuation' | null>(null);

  // Valuation modal
  const [valuationModalOpen, setValuationModalOpen] = useState(false);
  const [valuationCopro, setValuationCopro] = useState<{ id: number; address: string; cp: string; city: string } | null>(null);

  // Qualification criteria (inline assistant)
  const [bulkQualCriteria, setBulkQualCriteria] = useState<{ highVolume: boolean; highValue: boolean; manySci: boolean; highCommission: boolean }>({ highVolume: false, highValue: false, manySci: false, highCommission: false });
  const [rowQualificationOpen, setRowQualificationOpen] = useState(false);
  const [qualifRow, setQualifRow] = useState<{ id: number; name: string; immatriculation: string } | null>(null);
  const [rowQualification, setRowQualification] = useState<'oui' | 'non' | 'pas-qualifie' | null>(null);
  const [qualificationByCopro, setQualificationByCopro] = useState<Record<number, 'oui' | 'non' | 'pas-qualifie'>>({});
  const [boitageDateByCopro, setBoitageDateByCopro] = useState<Record<number, string>>({});

  type DvfSaleDetail = {
    nblot?: number | string;
    lots?: string;
    sbati?: number | string;
    datemut: string;
    libtypbien?: string;
    valeurfonc: number | string;
    buildingSurface?: number | string;
    rooms?: number | string;
  };

  const [isSalesPopupOpen, setSalesPopupOpen] = useState(false);
  const [coproSalesDetails, setCoproSalesDetails] = useState<DvfSaleDetail[] | null>(null);

  const headers = [
    { title: 'Immatriculation' },
    { title: 'Nom' },
    { title: 'Adresse' },
    { title: 'Ville' },
    { title: 'Montant total des ventes' },
    { title: 'Commission' },
    { title: 'Nb lots hab / local pro' },
    { title: 'Nb ventes' },
    { title: 'm2 moyen / vente' },
    { title: 'Prix moyen / m2' },
    { title: 'Nb SCI' },
    { title: 'Transactionnaire' },
    { title: 'Qualification Boîtage', sortField: 'Qualification Boîtage (sort)', sortable: true },
    { title: 'Boîtage fait', sortField: 'Boîtage fait (sort)', sortable: true },
    { title: 'Actions' },
  ];

  const fetchSciCounts = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/copros/scis/stats`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data: Array<{ id: number; scis: { total: number } }> = await resp.json();
      const map: Record<number, number> = {};
      data.forEach((c) => { map[c.id] = c.scis?.total ?? 0; });
      setSciCountByCopro(map);
    } catch (e) {
      console.error('Error fetching SCI counts :', e);
    }
  };

  const fetchTransactionnaires = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/validated-copros/attributions`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      const map: Record<number, string[]> = {};
      type AttributionItem = { fonctionType?: number; copro?: { id: number }; user?: { firstname?: string; lastname?: string } };
      (data?.results || []).forEach((a: AttributionItem) => {
        if (a?.fonctionType === TRANSACTIONNAIRE_FONCTION_TYPE && a?.copro?.id && a?.user) {
          const name = `${a.user.firstname} ${a.user.lastname ? a.user.lastname.toUpperCase() : ''}`.trim();
          if (!map[a.copro.id]) { map[a.copro.id] = []; }
          map[a.copro.id].push(name);
        }
      });
      setTransactionnaireByCopro(map);
    } catch (e) {
      console.error('Error fetching attributions :', e);
    }
  };

  const fetchRows = async() => {
    if (!company?.id || !jwt || selectedYears.length === 0) {return;}
    setStatus('PENDING');
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      selectedYears.forEach((y) => params.append('years[]', String(y)));
      const resp = await fetch(`/api/companies/${company.id}/stats/copros/sales/years?${params.toString()}`, {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      type SalesApiRow = {
        id: number;
        immatriculation?: string;
        name?: string;
        address?: string;
        cp?: string;
        city?: string;
        salesAmount?: number;
        nbLots?: number;
        nbSales?: number;
        m2ForNbSales?: number | null;
        salesAvgForM2?: number | null;
      };
      const results = data.results || data;
      const mapped: CoproSalesRow[] = (Array.isArray(results) ? results : []).map((r: SalesApiRow) => ({
        id: r.id,
        immatriculation: r.immatriculation,
        name: r.name,
        address: r.address,
        cp: r.cp,
        city: r.city,
        salesAmount: Number(r.salesAmount || 0),
        nbLots: Number(r.nbLots || 0),
        nbSales: Number(r.nbSales || 0),
        m2ForNbSales: r.m2ForNbSales != null ? Number(r.m2ForNbSales) : null,
        salesAvgForM2: r.salesAvgForM2 != null ? Number(r.salesAvgForM2) : null,
      }));
      setRows(mapped);
      setStatus('SUCCESS');
    } catch (e) {
      console.error('Error fetching sales rows :', e);
      setStatus('ERROR');
      setErrorMessage('Une erreur est survenue lors du chargement des données.');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  const fetchBoitageMaps = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/copros?limit=10000`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      const results = data?.results || [];
      const qMap: Record<number, 'oui' | 'non' | 'pas-qualifie'> = {};
      const dMap: Record<number, string> = {};
      type CoproBoitageItem = { id?: number; boitageQualification?: string; boitageDate?: string };
      results.forEach((c: CoproBoitageItem) => {
        if (c?.id) {
          const q = c.boitageQualification as string | null;
          if (q === 'oui' || q === 'non' || q === 'pas-qualifie') {
            qMap[c.id] = q;
          }
          if (c?.boitageDate) {
            const dateStr = (typeof c.boitageDate === 'string') ? c.boitageDate.substring(0, 10) : '';
            if (dateStr) { dMap[c.id] = dateStr; }
          }
        }
      });
      setQualificationByCopro(qMap);
      setBoitageDateByCopro(dMap);
    } catch (e) {
      console.error('Error fetching boitage maps :', e);
    }
  };

  useEffect(() => {
    if (yearsOptions.length > 0 && selectedYears.length === 0) {
      const firstTwo = yearsOptions.slice(0, 2).map((o) => Number(o.value));
      setSelectedYears(firstTwo);
      setLastDvfYear(Number(yearsOptions[0].value));
    }
  }, [yearsOptions]);

  useEffect(() => {
    fetchRows();
    fetchSciCounts();
    fetchTransactionnaires();
    fetchBoitageMaps();
  }, [company?.id, jwt, selectedYears.join(',')]);

  const fetchYearlyCompanyStats = useCallback(async() => {
    if (!company?.id || !jwt) { return; }
    setYearlyStatus('PENDING');
    try {
      const resp = await fetch(`/api/companies/${company.id}/stats/sales`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      // Expecting an object keyed by year
      const mapped: Record<number, { nbSales: number; nbLots: number; nbDpe: number; salesAmount: number; salesAveragePrice: number }> = {};
      Object.keys(data || {}).forEach((y) => {
        const yearNum = Number(y);
        const v = data[y];
        if (v && typeof v === 'object') {
          mapped[yearNum] = {
            nbSales: Number(v.nbSales || 0),
            nbLots: Number(v.nbLots || 0),
            nbDpe: Number(v.nbDpe || 0),
            salesAmount: Number(v.salesAmount || 0),
            salesAveragePrice: Number(v.salesAveragePrice || 0),
          };
        }
      });
      setYearlyStats(mapped);
      setYearlyStatus('SUCCESS');
    } catch (e) {
      console.error('Error fetching yearly company stats :', e);
      setYearlyStatus('ERROR');
    }
  }, [company?.id, jwt]);

  useEffect(() => {
    if (company?.id && jwt) {
      fetchYearlyCompanyStats();
    }
  }, [company?.id, jwt, fetchYearlyCompanyStats]);

  // Keep local commissionRate in sync with Redux company commission
  useEffect(() => {
    if (company?.commission != null) {
      setCommissionRate(company.commission);
    }
  }, [company?.commission]);

  const getCoproSalesDetails = async(id: number) => {
    const params = new URLSearchParams();
    selectedYears.forEach((y) => params.append('years[]', String(y)));
    try {
      const response = await fetch(`/api/copros/${id}/stats/sales/details/years?${params.toString()}`, JWTHeader(jwt));
      if (!response.ok) { throw new Error('Error fetching data'); }
      const detailsData = await response.json();
      type DvfApiEntry = { nblot?: number; lots?: string; sbati?: number; datemut: string; libtypbien?: string; valeurfonc?: number; buildingSurface?: number; rooms?: number };
      const formattedData = detailsData.map((dvf_entry: DvfApiEntry) => ({
        nblot: dvf_entry.nblot,
        lots: dvf_entry.lots,
        sbati: dvf_entry.sbati,
        datemut: dvf_entry.datemut,
        libtypbien: dvf_entry.libtypbien,
        valeurfonc: dvf_entry.valeurfonc,
        buildingSurface: dvf_entry.buildingSurface,
        rooms: dvf_entry.rooms,
      }));
      setCoproSalesDetails(formattedData);
    } catch (error) {
      console.error('Error fetching copro sales details :', error);
      setSalesPopupOpen(false);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  // Persist boitage inline edits
  const persistBoitage = async(coproId: number, qualification: 'oui' | 'non' | 'pas-qualifie' | null, dateStr: string | null) => {
    try {
      const payload: { boitageQualification?: 'oui' | 'non' | 'pas-qualifie'; boitageDate?: string | null } = {};
      if (qualification) { payload.boitageQualification = qualification; }
      if (dateStr !== undefined) { payload.boitageDate = dateStr; }
      const response = await fetch(`/api/copros/${coproId}/boitage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      // Show success toast
      showToast('success', 'Informations de boîtage enregistrées avec succès.');
    } catch (e) {
      console.error('Error persisting boitage :', e);
      showToast('error', 'Une erreur est survenue lors de l\'enregistrement des informations de boîtage.');
    }
  };

  // removed legacy manual year add; now using multiselect fed by DVF

  const resetFilters = () => {
    setFilters({ ventesMin: null, ventesMax: null, caMin: null, caMax: null, commMin: null, commMax: null, avgSaleMin: null, scisMin: null, transactionnaire: null });
    setBulkQualCriteria({ highVolume: false, highValue: false, manySci: false, highCommission: false });
  };

  // Deterministic assistant preview count
  const computeCountWithFilters = useCallback((candidate: Filters) => {
    const q = search.toLowerCase();
    const rate = commissionRate / 100;
    let n = 0;
    for (const r of rows) {
      const commission = r.salesAmount * rate;
      const txNames = transactionnaireByCopro[r.id] || [];
      const sciCount = sciCountByCopro[r.id] ?? 0;
      const avgSale = r.nbSales > 0 ? (r.salesAmount / r.nbSales) : 0;
      const basic = `${r.immatriculation} ${r.name} ${r.address} ${r.city} ${r.cp}`.toLowerCase();
      if (q && !basic.includes(q)) { continue; }
      if (candidate.ventesMin != null && r.nbSales < candidate.ventesMin) { continue; }
      if (candidate.ventesMax != null && r.nbSales > candidate.ventesMax) { continue; }
      if (candidate.caMin != null && r.salesAmount < candidate.caMin) { continue; }
      if (candidate.caMax != null && r.salesAmount > candidate.caMax) { continue; }
      if (candidate.commMin != null && commission < candidate.commMin) { continue; }
      if (candidate.commMax != null && commission > candidate.commMax) { continue; }
      if (candidate.avgSaleMin != null && avgSale < candidate.avgSaleMin) { continue; }
      if (candidate.scisMin != null && sciCount < candidate.scisMin) { continue; }
      if (candidate.transactionnaire && !txNames.includes(candidate.transactionnaire)) { continue; }
      n += 1;
    }
    return n;
  }, [rows, search, commissionRate, transactionnaireByCopro, sciCountByCopro]);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    const rate = commissionRate / 100;
    return rows.filter((r) => {
      const commission = r.salesAmount * rate;
      const txNames = transactionnaireByCopro[r.id] || [];
      const sciCount = sciCountByCopro[r.id] ?? 0;
      const avgSale = r.nbSales > 0 ? (r.salesAmount / r.nbSales) : 0;
      const basic = `${r.immatriculation} ${r.name} ${r.address} ${r.city} ${r.cp}`.toLowerCase();
      if (q && !basic.includes(q)) { return false; }
      if (filters.ventesMin != null && r.nbSales < filters.ventesMin) { return false; }
      if (filters.ventesMax != null && r.nbSales > filters.ventesMax) { return false; }
      if (filters.caMin != null && r.salesAmount < filters.caMin) { return false; }
      if (filters.caMax != null && r.salesAmount > filters.caMax) { return false; }
      if (filters.commMin != null && commission < filters.commMin) { return false; }
      if (filters.commMax != null && commission > filters.commMax) { return false; }
      if (filters.avgSaleMin != null && avgSale < filters.avgSaleMin) { return false; }
      if (filters.scisMin != null && sciCount < filters.scisMin) { return false; }
      if (filters.transactionnaire && !txNames.includes(filters.transactionnaire)) { return false; }
      return true;
    });
  }, [rows, search, filters, commissionRate, transactionnaireByCopro, sciCountByCopro]);

  const handleExportCsv = () => {
    const rate = commissionRate / 100;
    const headersCsv = ['Immatriculation', 'Nom', 'Adresse', 'Ville', 'Montant total des ventes', 'Commission', 'Nb lots hab / local pro', 'Nb ventes', 'm2 moyen / vente', 'Prix moyen / m2', 'Nb SCI', 'Transactionnaire', 'Qualification Boîtage', 'Boîtage fait'];
    const lines = [headersCsv.join(',')];
    filteredRows.forEach((r) => {
      const commission = r.salesAmount * rate;
      const tx = (transactionnaireByCopro[r.id] || []).join(' / ');
      const sci = sciCountByCopro[r.id] ?? '';
      const qual = qualificationByCopro[r.id] === 'oui' ? 'Oui' : qualificationByCopro[r.id] === 'non' ? 'Non' : 'Pas qualifié';
      const date = boitageDateByCopro[r.id] || '';
      const row = [
        r.immatriculation,
        r.name,
        r.address,
        `${r.city} - ${r.cp}`,
        String(Math.round(r.salesAmount)),
        String(Math.round(commission)),
        String(r.nbLots ?? ''),
        String(r.nbSales ?? ''),
        r.m2ForNbSales != null ? String(r.m2ForNbSales) : '',
        r.salesAvgForM2 != null ? String(Math.round(r.salesAvgForM2)) : '',
        String(sci),
        tx,
        qual,
        date,
      ];
      const escaped = row.map((v) => {
        const s = v == null ? '' : String(v);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
          return `"${s.replaceAll('"', '""')}"`;
        }
        return s;
      });
      lines.push(escaped.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().split('T')[0];
    a.download = `copro-boitage-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Toggle a predefined qualification criterion and immediately apply filters
  const toggleQualificationCriterion = (key: 'highVolume' | 'highValue' | 'manySci' | 'highCommission') => {
    setBulkQualCriteria((current) => {
      const updated = { ...current, [key]: !current[key] };
      setFilters((prev) => {
        // Start from previous filters but reset assistant-related fields
        const next: Filters = {
          ...prev,
          ventesMin: null,
          avgSaleMin: null,
          scisMin: null,
          commMin: null,
        };
        if (updated.highVolume) { next.ventesMin = Math.max(next.ventesMin ?? 0, 21); }
        if (updated.highValue) { next.avgSaleMin = Math.max(next.avgSaleMin ?? 0, 500000); }
        if (updated.manySci) { next.scisMin = Math.max(next.scisMin ?? 0, 15); }
        if (updated.highCommission) { next.commMin = Math.max(next.commMin ?? 0, 500000); }
        const count = computeCountWithFilters(next);
        showToast('success', `Qualification appliquée ! ${count} copropriété(s) qualifiée(s) pour le boîtage.`);
        return next;
      });
      return updated;
    });
  };

  const kpis = useMemo(() => {
    const totalCopros = filteredRows.length;
    const totalLotsEnGestion = filteredRows.reduce((s, r) => s + (r.nbLots || 0), 0);
    const totalSalesAmount = filteredRows.reduce((s, r) => s + (r.salesAmount || 0), 0);
    const totalLotsVendus = filteredRows.reduce((s, r) => s + (r.nbSales || 0), 0);
    const avgSalePrice = totalLotsVendus > 0 ? Math.round(totalSalesAmount / totalLotsVendus) : 0;
    return { totalCopros, totalLotsEnGestion, totalLotsVendus, avgSalePrice, totalSalesAmount };
  }, [filteredRows]);

  const dataForTable = useMemo(() => {
    const rate = commissionRate / 100;
    return filteredRows.map((r) => ({
      'Immatriculation': r.immatriculation,
      'Nom': (
        <div className="flex items-center gap-2">
          <RedirectionCoproButtons coproId={r.id} />
          <span className="font-semibold">{r.name}</span>
        </div>
      ),
      'Adresse': r.address,
      'Ville': `${r.city} - ${r.cp}`,
      'Montant total des ventes': formatCurrency(r.salesAmount),
      'Commission': formatCurrency(r.salesAmount * rate),
      'Nb lots hab / local pro': r.nbLots,
      'Nb ventes': r.nbSales,
      'm2 moyen / vente': r.m2ForNbSales != null ? formatValue(r.m2ForNbSales) : '-',
      'Prix moyen / m2': r.salesAvgForM2 != null ? formatCurrency(r.salesAvgForM2) : '-',
      'Nb SCI': sciCountByCopro[r.id] ?? '-',
      'Transactionnaire': (
        <span className="text-xs font-medium text-purple-600">{(transactionnaireByCopro[r.id] || []).join(', ')}</span>
      ),
      'Qualification Boîtage': (
        <span className={`rounded-2xl px-2 py-1 text-xs font-semibold ${
          qualificationByCopro[r.id] === 'oui' ? 'border border-green-400 bg-green-100 text-green-700' :
          qualificationByCopro[r.id] === 'non' ? 'border border-red-400 bg-red-100 text-red-700' :
          'border border-amber-400 bg-amber-100 text-amber-700'
        }`}>
          {qualificationByCopro[r.id] === 'oui' ? 'Oui' : qualificationByCopro[r.id] === 'non' ? 'Non' : 'Pas qualifié'}
        </span>
      ),
      'Boîtage fait': (
        <input
          type="date"
          className="w-28 rounded border border-gray-300 px-2 py-1 text-xs"
          value={boitageDateByCopro[r.id] || ''}
          onChange={(e) => {
            const v = e.target.value;
            setBoitageDateByCopro((prev) => ({ ...prev, [r.id]: v }));
            persistBoitage(r.id, qualificationByCopro[r.id] || null, v || null);
          }}
        />
      ),
      'Qualification Boîtage (sort)': (qualificationByCopro[r.id] ?? 'zzz').toString(),
      'Boîtage fait (sort)': boitageDateByCopro[r.id] || '',
      'Actions': (
        <div className="flex flex-col items-center gap-1">
          <button className="w-64 rounded bg-slate-800 px-3 py-1.5 text-[11px] text-white hover:bg-slate-700" onClick={() => { setSalesPopupOpen(true); getCoproSalesDetails(r.id); }}>Détail des ventes</button>
          <button className="w-64 rounded bg-red-600 px-3 py-1.5 text-[11px] text-white hover:bg-red-700" onClick={() => { setReportModalCopro({ id: r.id, name: r.name, immatriculation: r.immatriculation, address: r.address, cp: r.cp, city: r.city }); setSelectedReport(null); setReportModalOpen(true); }}>Rapports/ Avis de valeur</button>
          <button className="w-64 rounded bg-purple-600 px-3 py-1.5 text-[11px] text-white hover:bg-purple-700 disabled:opacity-50" disabled={(sciCountByCopro[r.id] ?? 0) === 0} onClick={() => navigate(ROUTES.MONITORING_SCIS, { state: { coproId: r.id } })}>Les SCI de la copro</button>
          <button className="w-64 rounded bg-emerald-600 px-3 py-1.5 text-[11px] text-white hover:bg-emerald-700" onClick={() => navigate(ROUTES.ANALYZING_COPRO_MANAGING_ID.replace(':coproId', String(r.id)))}>Voir la fiche détaillée de la copro</button>
          <button className="w-64 rounded bg-orange-600 px-3 py-1.5 text-[11px] text-white hover:bg-orange-700" onClick={() => { setQualifRow({ id: r.id, name: r.name, immatriculation: r.immatriculation }); setRowQualification(qualificationByCopro[r.id] || 'pas-qualifie'); setRowQualificationOpen(true); }}>Qualification copro pour boîtage</button>
        </div>
      ),
    }));
  }, [filteredRows, commissionRate, sciCountByCopro, transactionnaireByCopro, qualificationByCopro, boitageDateByCopro, navigate]);

  const handleGoBack = () => {
    try {
      const historyState = window.history.state as { idx?: number } | null;
      if (historyState && historyState.idx != null && historyState.idx > 0) {
        navigate(-1);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch {
      navigate(ROUTES.HOME);
    }
  };

  const handleReportNavigate = (kind: 'general' | 'sales' | 'dpe') => {
    const coproId = reportModalCopro?.id;
    if (!coproId) {
      showToast('error', 'Copropriété non sélectionnée.');
      return;
    }
    if (kind === 'general') {
      navigate(ROUTES.ANALYZING_GENERATE_COPRO_REPORT, { state: { selectedCoproId: coproId } });
    } else if (kind === 'sales') {
      navigate(ROUTES.ANALYZING_GENERATE_SALES_REPORT, { state: { selectedCoproId: coproId } });
    } else {
      navigate(ROUTES.PROSPECTING_GENERATE_DPE_REPORT, { state: { selectedCoproId: coproId } });
    }
    setReportModalOpen(false);
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-md md:p-8">
      <div className="mb-4 flex items-center justify-between">
        <button onClick={handleGoBack} className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark">
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>
        <DismissibleModuleTutorialPopup tutorial="referent-prospecting" label="Guide d'utilisation" />
      </div>

      <div className="mb-6 rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold md:text-3xl">FICHE DÉTAILLÉE DE VOS COPROPRIÉTÉS</h1>
            <div className="opacity-90">Analyse complète des performances et opportunités de ciblage</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:!grid-cols-5">
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : kpis.totalCopros}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Nombre de Copropriétés</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : kpis.totalLotsEnGestion}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Total Lots en Gestion</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : kpis.totalLotsVendus}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Lots Vendus {selectedYears.length > 0 ? (selectedYears.length === 1 ? `${selectedYears[0]}` : `${Math.max(...selectedYears)}-${Math.min(...selectedYears)}`) : ''}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : formatCurrency(kpis.avgSalePrice)}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Prix Moyen des Ventes</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : formatCurrency(kpis.totalSalesAmount)}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Volume Transactions {selectedYears.length > 0 ? (selectedYears.length === 1 ? `${selectedYears[0]}` : `${Math.max(...selectedYears)}-${Math.min(...selectedYears)}`) : ''}</div>
        </div>
      </div>

      {/* Controls */}
      {/* Move expandable section above filters, like PO initial implementation */}
      {/* Expandable: Yearly sales over last 4 years */}
      <div className="mb-3">
        <div className="overflow-hidden rounded-lg border">
          <button
            type="button"
            onClick={() => setYearlyExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors"
            style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
          >
            <span className="font-medium text-slate-700">Voir les chiffres clés des ventes par an depuis 4 ans</span>
            <span className={`text-slate-500 transition-transform ${yearlyExpanded ? 'rotate-180' : ''}`}>▼</span>
          </button>

          {/* Content */}
          {yearlyExpanded && (
            <div className="border-t bg-white">
              {/* Commission selector */}
              <div className="m-4 rounded p-4 text-white" style={{ background: '#3b82f6' }}>
                <h4 className="mb-2 text-center text-sm font-semibold">QUELLE EST VOTRE COMMISSION ?</h4>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {[3, 4, 5, 6, 7, 8].map((r) => (
                    <label key={r} className="flex cursor-pointer items-center gap-2 rounded bg-white/20 px-3 py-1 text-sm font-medium hover:bg-white/30">
                      <input
                        type="radio"
                        name="commission-rate"
                        className="accent-blue-600"
                        checked={commissionRate === r}
                        onChange={() => {
                          setCommissionRate(r);
                          if (company?.id && jwt) {
                            fetch(`/api/companies/${company.id}/market-settings`, {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
                              body: JSON.stringify({
                                commission: r,
                                realSales: company.realSales,
                                nbMandates: company.nbMandates,
                                nbPreSaleStatements: company.nbPreSaleStatements,
                                pricePreSaleStatements: company.pricePreSaleStatements,
                                nbSaleStatements: company.nbSaleStatements,
                              }),
                            }).then((resp) => {
                              if (!resp.ok) { throw new Error('Failed to update commission'); }
                              dispatch(setCompanyCommission(r));
                            }).catch((err) => {
                              console.error('Error updating commission :', err);
                              showToast('error', 'Une erreur est survenue lors de l\'enregistrement des données.');
                            });
                          }
                        }}
                      />
                      <span>{r}%</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Yearly table */}
              <div className="mx-4 mb-4">
                <h4 className="rounded-t bg-slate-800 px-4 py-3 text-sm font-semibold text-white">INFORMATIONS SUR VOTRE PARC</h4>
                <div className="overflow-x-auto rounded-b border">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="border-r px-4 py-3 text-left font-semibold text-slate-700"></th>
                        {(() => {
                          const todayYear = new Date().getFullYear();
                          const lastYear = lastDvfYear ?? todayYear;
                          const years = [lastYear - 3, lastYear - 2, lastYear - 1, lastYear];
                          return years.map((y) => {
                            const label = (y === todayYear) ? `${y} (en cours d'enregistrement)` : `${y}`;
                            return <th key={y} className="border-r px-4 py-3 text-center font-semibold text-slate-700">{label}</th>;
                          });
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Nb de ventes */}
                      <tr>
                        <td className="border-t bg-slate-50 px-4 py-3 font-medium text-slate-700">Nb de ventes dans vos copros</td>
                        {(() => {
                          const lastYear = lastDvfYear ?? new Date().getFullYear();
                          return [lastYear - 3, lastYear - 2, lastYear - 1, lastYear].map((y) => (
                            <td key={y} className="border-t px-4 py-3 text-center">{yearlyStats[y]?.nbSales ? formatValue(yearlyStats[y].nbSales) : '-'}</td>
                          ));
                        })()}
                      </tr>
                      {/* Nb DPE */}
                      <tr className="bg-amber-50">
                        <td className="border-t bg-slate-50 px-4 py-3 font-medium text-slate-700">Nb de DPE établis dans vos copros</td>
                        {(() => {
                          const lastYear = lastDvfYear ?? new Date().getFullYear();
                          return [lastYear - 3, lastYear - 2, lastYear - 1, lastYear].map((y) => (
                            <td key={y} className="border-t px-4 py-3 text-center">{yearlyStats[y]?.nbDpe ? formatValue(yearlyStats[y].nbDpe) : '-'}</td>
                          ));
                        })()}
                      </tr>
                      {/* Prix moyen des ventes */}
                      <tr>
                        <td className="border-t bg-slate-50 px-4 py-3 font-medium text-slate-700">Prix moyen des ventes dans vos copros</td>
                        {(() => {
                          const lastYear = lastDvfYear ?? new Date().getFullYear();
                          return [lastYear - 3, lastYear - 2, lastYear - 1, lastYear].map((y) => (
                            <td key={y} className="border-t px-4 py-3 text-center">{yearlyStats[y] ? formatCurrency(Math.round(yearlyStats[y].salesAveragePrice)) : '-'}</td>
                          ));
                        })()}
                      </tr>
                      {/* Total des ventes */}
                      <tr>
                        <td className="border-t bg-slate-50 px-4 py-3 font-medium text-slate-700">Total des ventes sur vos copros</td>
                        {(() => {
                          const lastYear = lastDvfYear ?? new Date().getFullYear();
                          return [lastYear - 3, lastYear - 2, lastYear - 1, lastYear].map((y) => (
                            <td key={y} className="border-t px-4 py-3 text-center">{yearlyStats[y] ? formatCurrency(Math.round(yearlyStats[y].salesAmount)) : '-'}</td>
                          ));
                        })()}
                      </tr>
                      {/* Commission row */}
                      <tr className="bg-slate-800 text-white">
                        <td className="border-t px-4 py-3 font-semibold">Commission à {commissionRate}%</td>
                        {(() => {
                          const lastYear = lastDvfYear ?? new Date().getFullYear();
                          const rate = commissionRate / 100;
                          return [lastYear - 3, lastYear - 2, lastYear - 1, lastYear].map((y) => {
                            const amount = yearlyStats[y]?.salesAmount ?? 0;
                            return <td key={y} className="border-t px-4 py-3 text-center font-semibold">{formatCurrency(Math.round(amount * rate))}</td>;
                          });
                        })()}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {yearlyStatus === 'PENDING' && (
                <div className="px-6 pb-4"><Badge mode="warning" text="Chargement des chiffres annuels..." /></div>
              )}
              {yearlyStatus === 'ERROR' && (
                <div className="px-6 pb-4"><Badge mode="error" text="Impossible de charger les chiffres annuels" /></div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 rounded-lg border bg-white shadow-sm">
        <div className="border-b p-5">
          <h3 className="mb-4 text-lg font-semibold text-slate-800">Liste de vos copropriétés avec leur fiches détaillées, les ventes, les rapports éditables, les sci présentes...</h3>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-6">
            <div className="flex items-start gap-2">
              <label className="mt-2 text-sm font-medium text-slate-600"><span role="img" aria-label="magnifying glass">🔍</span> Rechercher :</label>
              <input className="w-64 rounded border px-3 py-2 text-sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, ville, immatriculation..." />
            </div>
            <div className="flex items-start gap-2">
              <label className="mt-2 text-sm font-medium text-slate-600"><span role="img" aria-label="calendar">📅</span> Années :</label>
              <div className="min-w-[220px]">
                <SelectMe
                  id="years-select"
                  name="years"
                  value={selectedYears.map((y) => ({ value: String(y), label: String(y) }))}
                  onChange={(selectedOptions) => setSelectedYears(selectedOptions.map((o) => Number(o.value)))}
                  options={yearsOptions}
                  multiple
                  closeOnSelect={false}
                  listPosition="bottom"
                  placeholder="Sélectionnez une ou plusieurs années"
                />
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button className="rounded bg-slate-200 px-3 py-2 text-sm text-slate-800 hover:bg-slate-300" onClick={handleExportCsv}><span role="img" aria-label="chart">📊</span> Export</button>
            </div>
          </div>

          {/* Inline filters */}
          <div className="mt-4 rounded border p-4" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderColor: '#3b82f6' }}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="chart">📊</span> Nb ventes:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.ventesMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, ventesMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min" />
                <span>à</span>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.ventesMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, ventesMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="emoji">💰</span> CA:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.caMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, caMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
                <span>à</span>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.caMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, caMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="target">🎯</span> Commission:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.commMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, commMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
                <span>à</span>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.commMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, commMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="person">👤</span> Transactionnaire:</label>
                <select className="w-40 rounded border px-2 py-1 text-sm" value={filters.transactionnaire ?? ''} onChange={(e) => setFilters((f) => ({ ...f, transactionnaire: e.target.value || null }))}>
                  <option value="">Tous</option>
                  {[...new Set(Object.values(transactionnaireByCopro).flat().filter(Boolean))].map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="emoji">🏷️</span> Prix moyen/vente:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.avgSaleMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, avgSaleMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="factory">🏢</span> Nb SCI min:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.scisMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, scisMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min" />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button className="rounded bg-slate-500 px-3 py-1.5 text-sm text-white hover:bg-slate-600" onClick={resetFilters}><span role="img" aria-label="refresh">🔄</span> Réinitialiser</button>
              </div>
            </div>
            
            {/* Separator and Qualification Section */}
            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-3 text-sm font-medium text-gray-600">OU</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
            
            <div className="mt-4">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700"><span role="img" aria-label="target">🎯</span> Filtre de qualification prédéfini :</span>
                <span className="text-xs text-slate-500">Identifiez automatiquement les copropriétés prioritaires pour le boîtage</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  className={`rounded-2xl border px-3 py-1.5 text-xs ${bulkQualCriteria.highVolume ? 'border-blue-700 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
                  onMouseEnter={(e) => showQuickFilterTooltip(e, 'Copropriétés avec plus de 20 ventes par an')}
                  onMouseMove={(e) => showQuickFilterTooltip(e, 'Copropriétés avec plus de 20 ventes par an')}
                  onMouseLeave={hideQuickFilterTooltip}
                  onFocus={(e) => showQuickFilterTooltip(e as unknown as React.MouseEvent, 'Copropriétés avec plus de 20 ventes par an')}
                  onBlur={hideQuickFilterTooltip}
                  onClick={() => toggleQualificationCriterion('highVolume')}
                >
                  Fort volume
                </button>
                <button
                  className={`rounded-2xl border px-3 py-1.5 text-xs ${bulkQualCriteria.highValue ? 'border-blue-700 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
                  onMouseEnter={(e) => showQuickFilterTooltip(e, 'Transactions au-dessus de 500 000€ en moyenne')}
                  onMouseMove={(e) => showQuickFilterTooltip(e, 'Transactions au-dessus de 500 000€ en moyenne')}
                  onMouseLeave={hideQuickFilterTooltip}
                  onFocus={(e) => showQuickFilterTooltip(e as unknown as React.MouseEvent, 'Transactions au-dessus de 500 000€ en moyenne')}
                  onBlur={hideQuickFilterTooltip}
                  onClick={() => toggleQualificationCriterion('highValue')}
                >
                  Prix élevés
                </button>
                <button
                  className={`rounded-2xl border px-3 py-1.5 text-xs ${bulkQualCriteria.manySci ? 'border-blue-700 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
                  onMouseEnter={(e) => showQuickFilterTooltip(e, 'Plus de 15 SCI présentes dans la copropriété')}
                  onMouseMove={(e) => showQuickFilterTooltip(e, 'Plus de 15 SCI présentes dans la copropriété')}
                  onMouseLeave={hideQuickFilterTooltip}
                  onFocus={(e) => showQuickFilterTooltip(e as unknown as React.MouseEvent, 'Plus de 15 SCI présentes dans la copropriété')}
                  onBlur={hideQuickFilterTooltip}
                  onClick={() => toggleQualificationCriterion('manySci')}
                >
                  Beaucoup de SCI
                </button>
                <button
                  className={`rounded-2xl border px-3 py-1.5 text-xs ${bulkQualCriteria.highCommission ? 'border-blue-700 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
                  onMouseEnter={(e) => showQuickFilterTooltip(e, 'Commission potentielle supérieure à 500 000€')}
                  onMouseMove={(e) => showQuickFilterTooltip(e, 'Commission potentielle supérieure à 500 000€')}
                  onMouseLeave={hideQuickFilterTooltip}
                  onFocus={(e) => showQuickFilterTooltip(e as unknown as React.MouseEvent, 'Commission potentielle supérieure à 500 000€')}
                  onBlur={hideQuickFilterTooltip}
                  onClick={() => toggleQualificationCriterion('highCommission')}
                >
                  Commission élevée
                </button>
                {(bulkQualCriteria.highVolume || bulkQualCriteria.highValue || bulkQualCriteria.manySci || bulkQualCriteria.highCommission) && (
                  <button
                    className="ml-2 text-xs text-slate-600 underline"
                    onClick={() => {
                      setBulkQualCriteria({ highVolume: false, highValue: false, manySci: false, highCommission: false });
                      setFilters((prev) => ({
                        ...prev,
                        ventesMin: null,
                        avgSaleMin: null,
                        scisMin: null,
                        commMin: null,
                      }));
                      const count = computeCountWithFilters({ ...filters, ventesMin: null, avgSaleMin: null, scisMin: null, commMin: null });
                      showToast('success', `Qualification réinitialisée. ${count} copropriété(s) affichée(s).`);
                    }}
                  >
                    Tout effacer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        {status === 'ERROR' && (
          <div className="p-6"><Badge mode="error" text={errorMessage || 'Erreur'} /></div>
        )}
        {status === 'PENDING' && (
          <div className="p-6"><Badge mode="warning" text="Chargement..." /></div>
        )}
        {status === 'SUCCESS' && (
          <div className="p-4">
            <MUITable
              data={dataForTable}
              headers={headers}
              tableClass="min-w-full bg-white border text-xs"
              sortableColumns={['Immatriculation', 'Nom', 'Adresse', 'Ville', 'Montant total des ventes', 'Commission', 'Nb lots hab / local pro', 'Nb ventes', 'm2 moyen / vente', 'Prix moyen / m2', 'Nb SCI', 'Transactionnaire', 'Qualification Boîtage', 'Boîtage fait']}
              defaultSortKey="Nb ventes"
              defaultSortDirection="desc"
            />
            {dataForTable.length === 0 && (
              <div className="mx-6 my-6 text-center"><Badge mode="warning" text="Aucune copropriété trouvée" /></div>
            )}
          </div>
        )}
      </div>      

      {/* Report Modal */}
      <Popup
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        title="Rapports / Avis de valeur"
        additionalClass="w-full max-w-xl"
      >
        <div className="space-y-3">
          <div className="text-slate-600">Quel rapport souhaitez-vous éditer pour cette copropriété :</div>
          <label className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${selectedReport === 'general' ? 'border-blue-400 bg-blue-50' : ''}`} onClick={() => handleReportNavigate('general')}>
            <input type="radio" checked={selectedReport === 'general'} readOnly />
            <div>
              <div className="font-semibold text-slate-800">Présentation générale</div>
              <div className="text-sm text-slate-500">Vue d'ensemble de la copropriété, caractéristiques principales</div>
            </div>
          </label>
          <label className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${selectedReport === 'sales' ? 'border-blue-400 bg-blue-50' : ''}`} onClick={() => handleReportNavigate('sales')}>
            <input type="radio" checked={selectedReport === 'sales'} readOnly />
            <div>
              <div className="font-semibold text-slate-800">Rapport des ventes depuis 2014</div>
              <div className="text-sm text-slate-500">Analyse détaillée des transactions immobilières</div>
            </div>
          </label>
          <label className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${selectedReport === 'dpe' ? 'border-blue-400 bg-blue-50' : ''}`} onClick={() => handleReportNavigate('dpe')}>
            <input type="radio" checked={selectedReport === 'dpe'} readOnly />
            <div>
              <div className="font-semibold text-slate-800">Rapport des DPE depuis 2021</div>
              <div className="text-sm text-slate-500">Diagnostic de performance énergétique et évolution</div>
            </div>
          </label>
          <label
            className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${selectedReport === 'valuation' ? 'border-blue-400 bg-blue-50' : ''}`}
            onClick={() => {
              if (reportModalCopro) {
                setReportModalOpen(false);
                setValuationCopro({ id: reportModalCopro.id, address: reportModalCopro.address ?? '', cp: reportModalCopro.cp ?? '', city: reportModalCopro.city ?? '' });
                setValuationModalOpen(true);
              }
            }}
          >
            <input type="radio" checked={selectedReport === 'valuation'} readOnly />
            <div>
              <div className="font-semibold text-slate-800">Faire un avis de valeur pour un appartement</div>
              <div className="text-sm text-slate-500">Estimation immobilière détaillée d&apos;un bien spécifique</div>
            </div>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button className="rounded bg-slate-200 px-4 py-2 text-sm text-slate-800 hover:bg-slate-300" onClick={() => setReportModalOpen(false)}>Fermer</button>
          </div>
        </div>
      </Popup>

      {/* Fixed tooltip for quick filters */}
      {qfTooltip.visible && (
        <div
          className="pointer-events-none fixed z-[1000]"
          style={{ left: qfTooltip.x, top: qfTooltip.y }}
        >
          <div className="translate-x-1/2 translate-y-full">
            <div className="max-w-xs rounded-md bg-slate-800 px-3 py-2 text-[11px] leading-snug whitespace-normal text-white shadow-xl ring-1 ring-black/10">
              {qfTooltip.content}
            </div>
          </div>
        </div>
      )}

      

      {/* Row Qualification */}
      <Popup
        isOpen={isSalesPopupOpen}
        onClose={() => { setCoproSalesDetails(null); setSalesPopupOpen(false); }}
        title="Détails des ventes"
      >
        <div className="max-h-[80dvh] divide-y divide-dashed overflow-x-scroll">
          {coproSalesDetails && (
            <Table
              data={coproSalesDetails
                ? coproSalesDetails.map((item, index) => ({
                  id: index,
                  'Valeur Foncière': formatCurrency(Number(item.valeurfonc)),
                  Date: new Date(item.datemut).toLocaleDateString('fr-FR'),
                  'Type Bien': item.libtypbien,
                  'Surface habitale (m²)': item.buildingSurface,
                  'Nombre de Pièces': item.rooms,
                  'Nombre Lots': item.nblot,
                  'Numéros Lots': item.lots,
                }))
                : []}
              headers={['Valeur Foncière', 'Date', 'Type Bien', 'Surface habitale (m²)', 'Nombre de Pièces', 'Nombre Lots', 'Numéros Lots']}
              tableClass=""
              columnChecking={false}
              idKey="id"
              autoresize
              tbodyClass=""
              isSearchable={false}
              sortableColumns={{ 'Valeur Foncière': 'number', Date: 'date', Surface: 'number', 'Nombre de Pièces': 'string', 'Nombre Lots': 'number' }}
            />
          )}
        </div>
      </Popup>
      <Popup isOpen={rowQualificationOpen} onClose={() => setRowQualificationOpen(false)} title={<><span role="img" aria-label="target">🎯</span> Qualification Copropriété pour Boîtage</>} additionalClass="w-full max-w-xl">
        <div className="space-y-3">
          <div className="text-slate-500">Définissez le statut de qualification pour cette copropriété :</div>
          <label className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${rowQualification === 'oui' ? 'border-blue-400 bg-blue-50' : ''}`}>
            <input type="radio" name="q" checked={rowQualification === 'oui'} onChange={() => setRowQualification('oui')} />
            <div>
              <div className="font-semibold"><span role="img" aria-label="check mark">✅</span> Oui - Qualifiée pour boîtage</div>
              <div className="text-sm text-slate-500">Cette copropriété présente un potentiel intéressant et mérite d'être prospectée</div>
            </div>
          </label>
          <label className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${rowQualification === 'non' ? 'border-blue-400 bg-blue-50' : ''}`}>
            <input type="radio" name="q" checked={rowQualification === 'non'} onChange={() => setRowQualification('non')} />
            <div>
              <div className="font-semibold"><span role="img" aria-label="cross mark">❌</span> Non - Non qualifiée</div>
              <div className="text-sm text-slate-500">Cette copropriété ne correspond pas aux critères de ciblage</div>
            </div>
          </label>
          <label className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${rowQualification === 'pas-qualifie' ? 'border-blue-400 bg-blue-50' : ''}`}>
            <input type="radio" name="q" checked={rowQualification === 'pas-qualifie'} onChange={() => setRowQualification('pas-qualifie')} />
            <div>
              <div className="font-semibold">⏸️ Pas encore qualifiée</div>
              <div className="text-sm text-slate-500">Cette copropriété nécessite une analyse complémentaire</div>
            </div>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button className="rounded bg-slate-200 px-4 py-2 text-sm text-slate-800 hover:bg-slate-300" onClick={() => setRowQualificationOpen(false)}>Annuler</button>
            <button className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50" disabled={!qualifRow} onClick={async() => { if (qualifRow && rowQualification) { setQualificationByCopro((prev) => ({ ...prev, [qualifRow.id]: rowQualification })); await persistBoitage(qualifRow.id, rowQualification, boitageDateByCopro[qualifRow.id] || null); showToast('success', `Qualification mise à jour pour ${qualifRow.name}`); } setRowQualificationOpen(false); }}><span role="img" aria-label="check mark">✅</span> Confirmer la qualification</button>
          </div>
        </div>
      </Popup>

      {/* Valuation Modal */}
      {valuationCopro && (
        <ValuationModal
          isOpen={valuationModalOpen}
          onClose={() => { setValuationModalOpen(false); setValuationCopro(null); }}
          coproId={valuationCopro.id}
          coproPostalCode={valuationCopro.cp}
          coproCity={valuationCopro.city}
          coproAddress={valuationCopro.address}
        />
      )}
    </div>
  );
}

export default CoproDetailedList;
